import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { Container } from '@mui/system';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { url } from 'src/Helpers/utils/Constant';
import { IAgencyUpdateInfoApplication } from 'src/Types/module/applications/applications';
import { getStatusLabel } from 'src/Utils/Combined';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import UpdateAgencyInfoApplication from './UpdateAgencyInfoApplication';
import { Link } from 'react-router-dom';
import GlobalLoader from 'src/components/SuspenseLoader/GlobalLoader';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from 'src/components/pdfAndImageView/CommonTypoGraphy';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';

const labelWidth = { xs: '130px', sm: '160px', md: '185px' };

const SingleAgencyInfoUpdateApplication = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [application, setApplication] = useState<IAgencyUpdateInfoApplication>(
    {} as IAgencyUpdateInfoApplication,
  );
  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${url}/api/atab/agency-member-info/${id}`,
        );

        if (data.success) {
          setApplication(data.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
      }
    })();
  }, [id]);

  const agencyName = application?.user_member_company_name ? (
    <Link to={`/member-list/${application.agency_member_id}`}>
      {application.user_member_company_name}
    </Link>
  ) : null;

  const applicationDate = formatDate(
    application?.agency_info_created_at,
    'MMM Do YYYY',
  );

  const statusLabel = application?.status
    ? getStatusLabel(application.status)
    : 'N/A';

  const representativePhoto = application?.user_member_representative_photo ? (
    <CommonTypoGraphy
      name='View representative photo'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.user_member_representative_photo);
      }}
    />
  ) : null;

  const companyLogo = application?.member_company_logo ? (
    <CommonTypoGraphy
      name='View company logo'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.member_company_logo || null);
      }}
    />
  ) : null;

  const civilAviationFile = application?.civil_aviation_english_copy ? (
    <CommonTypoGraphy
      name='View civil aviation english copy'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.civil_aviation_english_copy || null);
      }}
    />
  ) : null;

  const tradeLicenseFile = application?.trade_license ? (
    <CommonTypoGraphy
      name='View trade license'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.trade_license || null);
      }}
    />
  ) : null;

  const forwardingLetterFile = application?.forwarding_letter ? (
    <CommonTypoGraphy
      name='View forwarding letter'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.forwarding_letter || null);
      }}
    />
  ) : null;

  const partnershipDeedFile = application?.partnership_deed ? (
    <CommonTypoGraphy
      name='View partnership deed'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.partnership_deed || null);
      }}
    />
  ) : null;

  const incorporationCertificateFile = application?.incorporation_certificate ? (
    <CommonTypoGraphy
      name='View incorporation certificate'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.incorporation_certificate || null);
      }}
    />
  ) : null;

  const moaFile = application?.moa ? (
    <CommonTypoGraphy
      name='View MoA'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.moa || null);
      }}
    />
  ) : null;

  const formXiiFile = application?.form_xii ? (
    <CommonTypoGraphy
      name='View Form XII'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.form_xii || null);
      }}
    />
  ) : null;

  const websiteLink = application?.member_company_website ? (
    <a
      href={
        application.member_company_website.startsWith('http')
          ? application.member_company_website
          : `https://${application.member_company_website}`
      }
      target='_blank'
      rel='noopener noreferrer'
    >
      {application.member_company_website}
    </a>
  ) : null;

  return (
    <>
      <Helmet>
        <title>Atab - Agency information update application</title>
      </Helmet>
      <Container maxWidth='xl'>
        {loading ? (
          <GlobalLoader />
        ) : (
          <Card>
            <CardHeader
              title='Agency information update application'
              action={
                <Button onClick={() => setShowModal(true)} variant='outlined'>
                  Update
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {application && Object.keys(application).length > 0 ? (
                <Grid container spacing={2} pt={2}>
                  {applicationDate && (
                    <CommonDetailItem
                      label='Application Date'
                      value={applicationDate}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.status && (
                    <CommonDetailItem
                      label='Status'
                      value={statusLabel}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {agencyName && (
                    <CommonDetailItem
                      label='Previous Agency Name'
                      value={agencyName}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}

                  <CommonDetailItem
                    label='Requested Agency Name'
                    value={formatValue(application.agency_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={6}
                  />

                  {application?.agency_email && (
                    <CommonDetailItem
                      label='Agency Email'
                      value={formatValue(application.agency_email)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.credential_email && (
                    <CommonDetailItem
                      label='New Credential Email'
                      value={formatValue(application.credential_email)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.user_member_representative_name && (
                    <CommonDetailItem
                      label='Representative Name'
                      value={formatValue(
                        application.user_member_representative_name,
                      )}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.user_member_representative_designation && (
                    <CommonDetailItem
                      label='Designation'
                      value={formatValue(
                        application.user_member_representative_designation,
                      )}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.phone && (
                    <CommonDetailItem
                      label='Mobile Number'
                      value={formatValue(application.phone)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.cellphone && (
                    <CommonDetailItem
                      label='Telephone Number'
                      value={formatValue(application.cellphone)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.member_company_website && (
                    <CommonDetailItem
                      label='Website'
                      value={websiteLink}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.user_member_representative_photo && (
                    <CommonDetailItem
                      label='Owner Photo'
                      value={representativePhoto}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.member_company_logo && (
                    <CommonDetailItem
                      label='Company Logo'
                      value={companyLogo}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}

                  {application?.ownership_status && (
                    <CommonDetailItem
                      label='Ownership Status'
                      value={formatValue(application.ownership_status)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.civil_aviation_english_copy && (
                    <CommonDetailItem
                      label='Civil Aviation'
                      value={civilAviationFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.trade_license && (
                    <CommonDetailItem
                      label='Trade License'
                      value={tradeLicenseFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.forwarding_letter && (
                    <CommonDetailItem
                      label='Forwarding Letter'
                      value={forwardingLetterFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.partnership_deed && (
                    <CommonDetailItem
                      label='Partnership Deed'
                      value={partnershipDeedFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.incorporation_certificate && (
                    <CommonDetailItem
                      label='Incorporation Cert'
                      value={incorporationCertificateFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.moa && (
                    <CommonDetailItem
                      label='MoA'
                      value={moaFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}
                  {application?.form_xii && (
                    <CommonDetailItem
                      label='Form XII'
                      value={formXiiFile}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={6}
                      md={6}
                    />
                  )}

                  {application?.note && (
                    <CommonDetailItem
                      label='Note'
                      value={formatValue(application.note)}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={12}
                      md={12}
                    />
                  )}
                  {application?.agency_info_update_rejeceted_reason && (
                    <CommonDetailItem
                      label='Reject Reason'
                      value={formatValue(
                        application.agency_info_update_rejeceted_reason,
                      )}
                      labelWidth={labelWidth}
                      xs={12}
                      sm={12}
                      md={12}
                    />
                  )}
                </Grid>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.primary'
                  display='inline'
                  sx={{ pr: 1 }}
                  gutterBottom
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
      <ModalsWrapper
        setShowModal={setShowModal}
        showModal={showModal}
        modalData={{
          title: 'Update agency information update application',
        }}
      >
        <UpdateAgencyInfoApplication
          setApplication={setApplication}
          application={application}
          handleClose={() => setShowModal(false)}
        />
      </ModalsWrapper>
      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />
    </>
  );
};

export default SingleAgencyInfoUpdateApplication;
